/** User-facing copy for OpCode editor (Simple vs Advanced labels). */

export type EditorMode = 'simple' | 'advanced';

export const STEP_KIND_LABELS: Record<string, { simple: string; advanced: string }> = {
    llm: { simple: 'Ask AI', advanced: 'LLM' },
    rag: { simple: 'Search knowledge', advanced: 'RAG' },
    api: { simple: 'Call API', advanced: 'API' },
    tool: { simple: 'Use tool', advanced: 'Tool' },
    memory: { simple: 'Conversation history', advanced: 'Memory' },
    choose: { simple: 'If / then', advanced: 'Choose' },
    subflow: { simple: 'Nested flow', advanced: 'SubFlow' },
    parallel: { simple: 'Run in parallel', advanced: 'Parallel' },
};

export const STEP_KIND_DESCRIPTIONS: Record<string, string> = {
    llm: 'Run a Library instruction (model + prompt) on an OEL input; output is @step_id.output.',
    rag: 'Retrieve relevant passages from a knowledge collection.',
    api: 'Call a configured REST API and use the response in later steps.',
    tool: 'Invoke an MCP tool or another opcode as a tool.',
    memory: 'Pull recent chat history into this procedure so later steps (usually AI) can see what was said.',
    choose: 'Branch to different steps based on a condition or expression.',
    subflow: 'Run a named reusable flow inside this procedure.',
    parallel: 'Run multiple branches at once. Each branch is a full step — use @branch_id.output in later steps. when / for / repeat_until go on branches, not the parallel step.',
};

export function stepKindLabel(kind: string, mode: EditorMode): string {
    const entry = STEP_KIND_LABELS[kind];
    if (!entry) return kind;
    return mode === 'simple' ? entry.simple : entry.advanced;
}

/** Step list label: technical kind + human description, e.g. "LLM : Ask AI". */
export function formatStepListLabel(step: { kind?: string; description?: string } | null | undefined): string {
    if (!step) return 'Step';
    const kind = step.kind || 'llm';
    const technical = stepKindLabel(kind, 'advanced');
    const description = (step.description && String(step.description).trim())
        || stepKindLabel(kind, 'simple');
    return `${technical} : ${description}`;
}

/** Kind picker / dropdown — always technical : friendly, e.g. "LLM : Ask AI". */
export function formatStepKindOptionLabel(kind: string): string {
    return `${stepKindLabel(kind, 'advanced')} : ${stepKindLabel(kind, 'simple')}`;
}

export const NAV_LABELS = {
    inputs: { simple: 'What it needs', advanced: 'Typed Inputs' },
    outputs: { simple: 'What it returns', advanced: 'Typed Outputs' },
    definitions: { simple: 'Reusable steps', advanced: 'Definitions' },
    flows: { simple: 'Reusable flows', advanced: 'Flows' },
    hooks: { simple: 'Hooks', advanced: 'Hooks' },
    manifest: { simple: 'Dependencies', advanced: 'Resource Manifest' },
    governance: { simple: 'Governance', advanced: 'Governance' },
    metadata: { simple: 'Details', advanced: 'Metadata' },
    steps: { simple: 'Procedure steps', advanced: 'Steps' },
};

export const FIELD_HELP = {
    title: 'A short name for this procedure — shown in the Library and assistant lists.',
    description: 'What this procedure does. Keep it clear for teammates.',
    category: 'Optional tag for grouping opcodes (e.g. customer-support).',
    version: 'Version of this definition (semver). Stored for your records.',
    defaultStepTimeout: 'Max milliseconds each step may run before timing out.',
    typedInputs: 'Values this procedure expects when it runs (e.g. order ID, question).',
    typedOutputs: 'Values this procedure returns after it finishes.',
};

export const EMPTY_CTA = {
    ragAnswer: {
        title: 'Answer from knowledge',
        description: 'Search your documents, then ask AI to respond.',
    },
    apiSummarize: {
        title: 'Call API and summarize',
        description: 'Fetch data from an API, then summarize with AI.',
    },
    llmOnly: {
        title: 'Ask AI',
        description: 'Single AI step — good for simple tasks.',
    },
};
